import { CaretRight } from '@phosphor-icons/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { ZARA_ENABLED } from '../../../const';
import { ZARA_ADVISOR_ALIAS } from '../../../service/advisorAccess';
import { markAdvisorIntroSeen } from '../../../store/slices/authSlice';
import { getZaraWelcomeModalState, setShowZaraWelcomeModal } from '../../../store/slices/modalsSlice';
import { gaEvents } from '../../../utils/analytics';
import { useStartHomeChat } from '../../pages/AnonHomePage/useStartHomeChat';
import ZaraModalShell from '../ZaraModalShell';
import styles from './styles.module.scss';

// Figma (mobile 431-3281 / desktop 561-1714) shows 4 chips on desktop but
// only 3 on the mobile frame ("Just read my chart" missing) — treated as a
// mobile-frame oversight and shipped identically on both breakpoints.
const PROMPT_KEYS = ['chip1', 'chip2', 'chip3', 'chip4'];

// One-time "first time in Zara" welcome screen — shown instead of going
// straight to chat the first time a premium subscriber taps Zara (see
// `shouldShowZaraWelcome` in advisorAccess.js, checked alongside
// `getAdvisorLockReason` at both entry points: MarketplaceActionButton and
// common/Menu/MenuContent). "Never see it more than once" is
// account-wide (backend `seenAdvisorIntros`, not per-device localStorage),
// so closing via X counts as seen exactly the same as tapping a chip.
const ZaraWelcomeModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isVisible = useSelector(getZaraWelcomeModalState);
    const { startHomeChat } = useStartHomeChat();

    // HORO-10504: previously the only tracked interaction here was a chip
    // tap (generic `home_chat_start`) — the modal being SEEN at all was
    // never recorded, so there was no way to compute a chip-tap rate.
    useEffect(() => {
        if (isVisible) {
            gaEvents.zaraWelcomeModalViewed();
        }
    }, [isVisible]);

    // HORO-10504: kill switch — checked after both hooks (Rules of Hooks)
    // but before any render output. Mounted unconditionally in App.js for
    // every logged-in user, so this is the one place that guarantees
    // nothing Zara-shaped renders regardless of what dispatched
    // `setShowZaraWelcomeModal(true)`.
    if (!ZARA_ENABLED) {
        return null;
    }

    const markSeenAndClose = () => {
        dispatch(markAdvisorIntroSeen(ZARA_ADVISOR_ALIAS));
        dispatch(setShowZaraWelcomeModal(false));
    };

    // Reuses the homepage's proven "create chat, send this as the first
    // message, then navigate" flow (useStartHomeChat) rather than
    // reimplementing it — the account is already a confirmed premium
    // subscriber by the time this modal can even be open, so that hook's
    // own anon-gate/message-limit checks are no-ops here in practice.
    const onPromptTap = (promptText) => {
        markSeenAndClose();
        startHomeChat({ content: promptText, advisorAlias: ZARA_ADVISOR_ALIAS, source: 'zaraWelcome' });
    };

    return (
        <ZaraModalShell isVisible={isVisible} onClose={markSeenAndClose}>
            <ul className={styles.prompts}>
                {PROMPT_KEYS.map((key) => (
                    <li key={key} className={styles.promptRow}>
                        <button type="button" className={styles.promptBtn} onClick={() => onPromptTap(t(`zaraWelcome.${key}`))}>
                            <span className={styles.promptText}>{t(`zaraWelcome.${key}`)}</span>
                            <CaretRight size={16} color="#FFFFFF" aria-hidden="true" />
                        </button>
                    </li>
                ))}
            </ul>
        </ZaraModalShell>
    );
};

export default ZaraWelcomeModal;
